import {AfterViewInit, Component, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {CustomerService} from '../../../../service/customer.service';
import {PeoService} from '../../../../service/peo.service';
import {PeoClientService} from '../../../../service/peo-client.service';
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {MatStep, MatStepper, MatStepperModule, MatStepperNext} from '@angular/material/stepper';
import {MatSnackBar, MatSnackBarRef, TextOnlySnackBar} from '@angular/material/snack-bar';
import {CustomerApiModel} from '../../../../model/customer-api.model';
import {PeoApiModel} from '../../../../model/peo-api.model';
import {KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatProgressBar} from '@angular/material/progress-bar';
import {NgxCsvParser, NgxCSVParserError} from 'ngx-csv-parser';
import {MatOption} from '@angular/material/autocomplete';
import {MatLabel, MatSelect} from '@angular/material/select';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef, MatNoDataRow, MatRow, MatRowDef,
  MatTable, MatTableDataSource
} from '@angular/material/table';
import {MatTooltip} from '@angular/material/tooltip';
import {UtilityService} from '../../../../service/utility.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../../../dialog/confirmation/confirmation.component';
import {PeoClientApiModel} from '../../../../model/peo-client-api.model';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {RouterLink} from "@angular/router";
import {PeoClientApiManyRequestModel} from "../../../../model/peo-client-api-many-request.model";

/**
 * @description Admin Import Wizard Page Component
 * @class ImportWizardAdminPageComponent
 * @export
 * @name ImportWizardAdminPageComponent
 */
@Component({
  imports: [
    MatCard,
    MatCardAvatar,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatIcon,
    MatStepper,
    MatStep,
    ReactiveFormsModule,
    MatError,
    MatHint,
    MatLabel,
    NgIf,
    MatProgressSpinner,
    MatButton,
    MatStepperModule,
    MatStepperNext,
    MatFormField,
    MatInput,
    MatProgressBar,
    MatOption,
    NgForOf,
    MatSelect,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTooltip,
    MatSuffix,
    MatIconButton,
    MatPaginator,
    FormsModule,
    MatNoDataRow,
    MatSlideToggle,
    KeyValuePipe,
    RouterLink
  ],
  selector: 'app-page-admin-import-wizard',
  standalone: true,
  styleUrl: './import-wizard.component.scss',
  templateUrl: './import-wizard.component.html'
})
export class ImportWizardAdminPageComponent implements AfterViewInit {

  /**
   * @description This property contains the client customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#clientCustomers
   * @property
   * @protected
   * @type {PeoClientApiModel[]}
   */
  protected clientCustomers: PeoClientApiModel[] = [];

  /**
   * @description This property contains the column selection form group for the column matching step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#columnSelectionFormGroup
   * @property
   * @protected
   * @readonly
   * @type {FormGroup<{
   *   clientIdColumn: FormControl<number>,
   *   customerIdColumn: FormControl<number>,
   *   peoIdColumn: FormControl<number>
   * }>}
   */
  protected readonly columnSelectionFormGroup: FormGroup<{
    clientIdColumn: FormControl<number>,
    customerIdColumn: FormControl<number>,
    peoIdColumn: FormControl<number>
  }>;

  /**
   * @default {}
   * @description This property contains the client customer toggles for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#clientCustomerToggles
   * @property
   * @protected
   * @type {{[key: string]: {
   *    checked: boolean,
   *    checking: boolean,
   *    error?: Error,
   *    exclude: boolean,
   *    saved: boolean,
   *    saving: boolean,
   *    unique: boolean
   *  }}}
   */
  protected clientCustomerToggles: {
    [key: string]: {
      checked: boolean,
      checking: boolean,
      error?: Error,
      exclude: boolean,
      saved: boolean,
      saving: boolean,
      unique: boolean
    }
  } = {};

  /**
   * @default []
   * @description This property contains the csv data for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csv
   * @property
   * @protected
   * @type {{}[]}
   */
  protected csv: {}[] = [];

  /**
   * @default ''
   * @description This property contains the name of the csv file selected for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csvFile
   * @property
   * @protected
   * @type {File | null}
   */
  protected csvFile: File | null = null;

  /**
   * @default false
   * @description This property denotes the parsing status of the csv file for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csvFileParsing
   * @property
   * @protected
   * @type {boolean}
   */
  protected csvFileParsing: boolean = false;

  /**
   * @default 0
   * @description This property contains the progress of the csv file upload for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csvFileUploadProgress
   * @property
   * @protected
   * @type {number}
   */
  protected csvFileUploadProgress: number = 0;

  /**
   * @default []
   * @description This property contains the headers of the csv file for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csvHeaders
   * @property
   * @protected
   * @type {string[]}
   */
  protected csvHeaders: string[] = [];

  /**
   * @default {}
   * @description This property contains the filtered headers of the csv file for the Client ID column.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csvHeadersFilteredClientId
   * @property
   * @protected
   * @type {{[key: number]: string}}
   */
  protected csvHeadersFilteredClientId: { [key: number]: string } = {};

  /**
   * @default {}
   * @description This property contains the filtered headers of the csv file for the Customer ID column.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csvHeadersFilteredCustomerId
   * @property
   * @protected
   * @type {{[key: number]: string}}
   */
  protected csvHeadersFilteredCustomerId: { [key: number]: string } = {};

  /**
   * @default {}
   * @description This property contains the filtered headers of the csv file for the PEO ID column.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#csvHeadersFilteredPeoId
   * @property
   * @protected
   * @type {{[key: number]: string}}
   */
  protected csvHeadersFilteredPeoId: { [key: number]: string } = {};

  /**
   * @default {}
   * @description This property contains the customer map for the preload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#customerMap
   * @property
   * @protected
   * @type {{[key: number]: CustomerApiModel}}
   */
  protected customerMap: { [key: number]: CustomerApiModel } = {};

  /**
   * @default false
   * @description This property denotes the downloading status of the csv file for the new client-customers.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#downloadingCsvNewClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected downloadingCsvNewClientCustomers: boolean = false;

  /**
   * @default false
   * @description This property denotes the downloading status of the csv file for the previously mapped client-customers.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#downloadingCsvPreviouslyMappedClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected downloadingCsvPreviouslyMappedClientCustomers: boolean = false;

  /**
   * @default false
   * @description This property denotes the downloading status of the csv file for the split client-customers.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#downloadingCsvSplitClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected downloadingCsvSplitClientCustomers: boolean = false;

  /**
   * @description This property contains the form group for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#fileUploadFormGroup
   * @property
   * @protected
   * @readonly
   * @type {FormGroup<{
   *   csv: FormControl<{}>
   * }>}
   */
  protected readonly fileUploadFormGroup: FormGroup<{
    csv: FormControl<boolean>
  }>;

  /**
   * @description This property contains the columns for the PEO Clients table that can be filtered.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#filterColumns
   * @property
   * @protected
   * @readonly
   * @type {(keyof PeoClientApiModel)[]}
   */
  protected readonly filterColumns: (keyof PeoClientApiModel)[] =
    ['peoId', 'clientId', 'customerId', 'customerName', 'customerBrandName'];

  /**
   * @default false
   * @description This property denotes the loading status of the customer map for the preload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#loadingCustomerMap
   * @property
   * @protected
   * @type {boolean}
   */
  protected loadingCustomerMap: boolean = false;

  /**
   * @default false
   * @description This property denotes the loading status of the new client customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#loadingNewClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected loadingNewClientCustomers: boolean = false;

  /**
   * @default false
   * @description This property denotes the loading status of the peo map for the preload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#loadingPeoMap
   * @property
   * @protected
   * @type {boolean}
   */
  protected loadingPeoMap: boolean = false;

  /**
   * @default false
   * @description This property denotes the loading status of the previously mapped client customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#loadingPreviouslyMappedClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected loadingPreviouslyMappedClientCustomers: boolean = false;

  /**
   * @default false
   * @description This property denotes the loading status of the split customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#loadingSplitClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected loadingSplitClientCustomers: boolean = false;

  /**
   * @default false
   * @description This property denotes the missing peo status for the peo validation step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#missingPeo
   * @property
   * @protected
   * @type {boolean}
   */
  protected missingPeo: boolean = false;

  /**
   * @default false
   * @description This property denotes the missing peo saved status for the peo validation step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#missingPeoSaved
   * @property
   * @protected
   * @type {boolean}
   */
  protected missingPeoSaved: boolean = false;

  /**
   * @default false
   * @description This property denotes the missing peo saving status for the peo validation step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#missingPeoSaving
   * @property
   * @protected
   * @type {boolean}
   */
  protected missingPeoSaving: boolean = false;

  /**
   * @default {}
   * @description This property contains the missing peo toggles for the peo validation step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#missingPeoToggles
   * @property
   * @protected
   * @type {{[key: string]: { error?: Error, saved: boolean, saving: boolean }}}
   */
  protected missingPeoToggles: { [key: string]: { error?: Error, saved: boolean, saving: boolean } } = {};

  /**
   * @default []
   * @description This property contains the split customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#newClientCustomers
   * @property
   * @protected
   * @type {MatTableDataSource<PeoClientApiModel>}
   */
  protected newClientCustomers: MatTableDataSource<PeoClientApiModel> =
    new MatTableDataSource<PeoClientApiModel>();

  /**
   * @description This property contains the filter text for the new client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#newClientCustomersFilterInput
   * @property
   * @protected
   * @type {string}
   */
  protected newClientCustomersFilterInput: string = UtilityService.EmptyString;

  /**
   * @description This property contains the sorter for the new client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#newClientCustomersSort
   * @property
   * @protected
   * @type {MatSort}
   */
  @ViewChild(MatSort)
  protected newClientCustomersSort!: MatSort;

  /**
   * @description This property contains the paginator elements from the view.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#paginators
   * @property
   * @protected
   * @type {QueryList<MatPaginator>}
   */
  @ViewChildren(MatPaginator) paginators!: QueryList<MatPaginator>;

  /**
   * @default {}
   * @description This property contains the peo map for the preload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#peoMap
   * @property
   * @protected
   * @type {{[key: string]: PeoApiModel}}
   */
  protected peoMap: { [key: string]: PeoApiModel } = {};

  /**
   * @description This property contains the form group for the peo validation step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#peoValidationFormGroup
   * @property
   * @protected
   * @readonly
   * @type {FormGroup<{
   *   items: FormArray<FormGroup<{
   *     id: FormControl<string>,
   *     name: FormControl<string>
   *   }>>
   * }>}
   */
  protected readonly peoValidationFormGroup: FormGroup<{
    items: FormArray<FormGroup<{
      id: FormControl<string>,
      name: FormControl<string>
    }>>
  }>;

  /**
   * @default []
   * @description This property contains the previously mapped clients for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#previouslyMappedClientCustomers
   * @property
   * @protected
   * @type {MatTableDataSource<PeoClientApiModel>}
   */
  protected previouslyMappedClientCustomers: MatTableDataSource<PeoClientApiModel> =
    new MatTableDataSource<PeoClientApiModel>();

  /**
   * @description This property contains the filter text for the previously mapped customers table.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#previouslyMappedClientCustomersFilterInput
   * @property
   * @protected
   * @type {string}
   */
  protected previouslyMappedClientCustomersFilterInput: string = UtilityService.EmptyString;

  /**
   * @description This property contains the sorter for the previously mapped customers table.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#previouslyMappedClientCustomersSort
   * @property
   * @protected
   * @type {MatSort}
   */
  @ViewChild(MatSort)
  protected previouslyMappedClientCustomersSort!: MatSort;

  /**
   * @description This property contains the review table columns for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#reviewTableColumns
   * @property
   * @protected
   * @readonly
   * @type {string[]}
   */
  protected readonly reviewTableColumns: string[] = [
    'peoId', 'clientId', 'customerId', 'customerName', 'customerBrandName'];

  /**
   * @description This property contains the review table columns for the new client customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#reviewTableColumnsForNewClientCustomers
   * @property
   * @protected
   * @readonly
   * @type {string[]}
   */
  protected readonly reviewTableColumnsForNewClientCustomers: string[] = [
    '_exclude', 'peoId', 'clientId', 'customerId', 'customerName', 'customerBrandName'
  ];

  /**
   * @default false
   * @description This property denotes the saved status of the new client-customer mappings.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#savedNewClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected savedNewClientCustomers: boolean = false;

  /**
   * @default false
   * @description This property denotes the saving status of the new client-customer mappings.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#savingNewClientCustomers
   * @property
   * @protected
   * @type {boolean}
   */
  protected savingNewClientCustomers: boolean = false;

  /**
   * @default true
   * @description This property denotes whether the customer's print settings should be
   *              reconfigured to the PrismHR settings when saving new client customers or not.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#savingNewClientCustomersIncludePrintSettings
   * @property
   * @protected
   * @type {boolean}
   */
  protected savingNewClientCustomersIncludePrintSettings: boolean = true;

  /**
   * @default []
   * @description This property contains the split customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#splitClientCustomers
   * @property
   * @protected
   * @readonly
   * @type {MatTableDataSource<PeoClientApiModel>}
   */
  protected splitClientCustomers: MatTableDataSource<PeoClientApiModel> = new MatTableDataSource<PeoClientApiModel>();

  /**
   * @description This property contains the filter text for the split customers table.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#splitClientCustomersFilterInput
   * @property
   * @protected
   * @type {string}
   */
  protected splitClientCustomersFilterInput: string = UtilityService.EmptyString;

  /**
   * @description This property contains the sorter for the split customers table.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#splitClientCustomersSort
   * @property
   * @protected
   * @type {MatSort}
   */
  @ViewChild(MatSort)
  protected splitClientCustomersSort!: MatSort;

  /**
   * @default 0
   * @description This property contains the total new client customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#totalNewClientCustomers
   * @property
   * @protected
   * @type {number}
   */
  protected totalNewClientCustomers: number = 0;

  /**
   * @default 0
   * @description This property contains the total previously mapped client customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#totalPreviouslyMappedClientCustomers
   * @property
   * @protected
   * @type {number}
   */
  protected totalPreviouslyMappedClientCustomers: number = 0;

  /**
   * @default 0
   * @description This property contains the total split client customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#totalSplitClientCustomers
   * @property
   * @protected
   * @type {number}
   */
  protected totalSplitClientCustomers: number = 0;

  /**
   * @constructor
   * @description This method is the constructor of the class ImportWizardAdminPageComponent.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#constructor()
   * @param {CustomerService} customerService The injected customer service provider.
   * @param {MatDialog} dialog The injected dialog provider.
   * @param {Papa} papa The injected papa provider.
   * @param {PeoClientService} peoClientService The injected peo client service provider.
   * @param {PeoService} peoService The injected peo service provider.
   * @param {MatSnackBar} snackbar The injected snackbar provider.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected customer service provider.
     * @memberof ImportWizardAdminPageComponent
     * @name ImportWizardAdminPageComponent#customerService
     * @private
     * @property
     * @readonly
     * @type {CustomerService}
     */
    private readonly customerService: CustomerService,
    /**
     * @description This property contains the injected dialog provider.
     * @memberof ImportWizardAdminPageComponent
     * @name ImportWizardAdminPageComponent#dialog
     * @private
     * @property
     * @readonly
     * @type {MatDialog}
     */
    private readonly dialog: MatDialog,
    /**
     * @description This property contains the injected CSV parser provider.
     * @memberof ImportWizardAdminPageComponent
     * @name ImportWizardAdminPageComponent#parser
     * @private
     * @property
     * @readonly
     * @type {NgxCsvParser}
     */
    private readonly parser: NgxCsvParser,
    /**
     * @description This property contains the injected peo client service provider.
     * @memberof ImportWizardAdminPageComponent
     * @name ImportWizardAdminPageComponent#peoClientService
     * @private
     * @property
     * @readonly
     * @type {PeoClientService}
     */
    private readonly peoClientService: PeoClientService,
    /**
     * @description This property contains the injected peo service provider.
     * @memberof ImportWizardAdminPageComponent
     * @name ImportWizardAdminPageComponent#peoService
     * @private
     * @property
     * @readonly
     * @type {PeoService}
     */
    private readonly peoService: PeoService,
    /**
     * @description This property contains the injected snackbar provider.
     * @memberof ImportWizardAdminPageComponent
     * @name ImportWizardAdminPageComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar
  ) {

    // Instantiate our column selection step form group.
    this.columnSelectionFormGroup = new FormGroup<{
      clientIdColumn: FormControl<number>,
      customerIdColumn: FormControl<number>,
      peoIdColumn: FormControl<number>
    }>({

      // Add the client id column form control to the form group.
      clientIdColumn: new FormControl<number>(-1, {

        // This form control is not nullable.
        nonNullable: true,

        // This form control is required to have a minimum length of 1.
        validators: [Validators.required, Validators.min(0)]
      }),

      // Add the customer id column form control to the form group.
      customerIdColumn: new FormControl<number>(-1, {

        // This form control is not nullable.
        nonNullable: true,

        // This form control is required to have a minimum length of 1.
        validators: [Validators.required, Validators.min(0)]
      }),

      // Add the peo id column form control to the form group.
      peoIdColumn: new FormControl<number>(-1, {

        // This form control is not nullable.
        nonNullable: true,

        // This form control is required to have a minimum length of 1.
        validators: [Validators.required, Validators.min(0)]
      })
    });

    // Instantiate our form file upload step form group.
    this.fileUploadFormGroup = new FormGroup<{
      csv: FormControl<boolean>
    }>({

      // Add the csv file form control to the form group.
      csv: new FormControl<boolean>(false, {

        // This form control is not nullable.
        nonNullable: true,

        // This form control is required to have a minimum length of 1.
        validators: [Validators.requiredTrue]
      })
    });

    // Instantiate our form peo validation step form group.
    this.peoValidationFormGroup = new FormGroup<{
      items: FormArray<FormGroup<{
        id: FormControl<string>,
        name: FormControl<string>
      }>>
    }>({

      // Add the peo validation items form array to the form group.
      items: new FormArray<FormGroup<{
        id: FormControl<string>,
        name: FormControl<string>
      }>>([])
    });

    // Add our split client-customers to the table data source filter predicate.
    this.splitClientCustomers.filterPredicate =
      UtilityService.TableMultiFilterPredicate<PeoClientApiModel>(this.filterColumns);
  }

  /**
   * @description This method is called when missing peo toggles change.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#checkMissingPeoToggles()
   * @private
   * @returns {void}
   */
  private checkMissingPeoToggles(): void {

    // Reset the missing PEO saved flag into the instance.
    this.missingPeoSaved = Object.keys(this.missingPeoToggles).every((k: string): boolean =>
      this.missingPeoToggles[k].saved || !!this.missingPeoToggles[k].error);

    // Reset the missing PEO saving flag into the instance.
    this.missingPeoSaving = Object.keys(this.missingPeoToggles).some((k: string): boolean =>
      this.missingPeoToggles[k].saving);
  }

  /**
   * @description This method is called to check the saving status of new client customers.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#checkNewClientCustomerToggles()
   * @private
   * @returns {void}
   */
  private checkNewClientCustomerToggles(): void {

    // Reset the saved status for the new client customers into the instance.
    this.savedNewClientCustomers = this.newClientCustomers.data.filter((c: PeoClientApiModel): boolean =>
      !this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].exclude).every((c: PeoClientApiModel): boolean =>
      this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].saved ||
      !!this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].error);

    // Reset the saving status for the new client customers into the instance.
    this.savingNewClientCustomers = this.newClientCustomers.data.filter((c: PeoClientApiModel): boolean =>
      !this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].exclude).some((c: PeoClientApiModel): boolean =>
      this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].saving);

    console.log('ClientCustomerStatus', {
      saved: this.savedNewClientCustomers,
      saving: this.savingNewClientCustomers,
    })
  }

  /**
   * @description This method is called when unique client customer toggles change.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#checkUniqueClientCustomerToggles()
   * @private
   * @returns {void}
   */
  private checkUniqueClientCustomerToggles(): void {

    // Reset the loading status for the new client customers into the instance.
    this.loadingNewClientCustomers = !Object.keys(this.clientCustomerToggles).every((k: string): boolean =>
      this.clientCustomerToggles[k].checked);

    // Reset the loading status for the previously mapped client customers into the instance.
    this.loadingPreviouslyMappedClientCustomers = !Object.keys(this.clientCustomerToggles).every((k: string): boolean =>
      this.clientCustomerToggles[k].checked);

    // Check for new client-customer loading complete.
    if (!this.loadingNewClientCustomers) {

      // Reset the data source on the new client customers table.
      this.newClientCustomers = new MatTableDataSource<PeoClientApiModel>(this.clientCustomers
        .filter((c: PeoClientApiModel): boolean => this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].unique));

      // Add our new client-customers to the table data source filter predicate.
      this.newClientCustomers.filterPredicate =
        UtilityService.TableMultiFilterPredicate<PeoClientApiModel>(this.filterColumns);

      // Set our new client-customers to the table data source paginator.
      this.newClientCustomers.paginator = this.paginators.toArray()[2];

      // Reset the paginator to the first page if it exists.
      this.newClientCustomers.paginator?.firstPage();

      // Reset the total new client customers into the instance.
      this.totalNewClientCustomers = this.newClientCustomers.data.length;
    }

    // Check for previously mapped client-customer loading complete.
    if (!this.loadingPreviouslyMappedClientCustomers) {

      // Reset the data source on the previously mapped client customers table.
      this.previouslyMappedClientCustomers = new MatTableDataSource<PeoClientApiModel>(this.clientCustomers
        .filter((c: PeoClientApiModel): boolean =>
          !this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].unique));

      // Add our previously mapped client-customers to the table data source filter predicate.
      this.previouslyMappedClientCustomers.filterPredicate =
        UtilityService.TableMultiFilterPredicate<PeoClientApiModel>(this.filterColumns);

      // Set our previously mapped client-customers to the table data source paginator.
      this.previouslyMappedClientCustomers.paginator = this.paginators.toArray()[1];

      // Reset the paginator to the first page if it exists.
      this.previouslyMappedClientCustomers.paginator?.firstPage();

      // Reset the total previously mapped client customers into the instance.
      this.totalPreviouslyMappedClientCustomers = this.previouslyMappedClientCustomers.data.length;
    }
  }

  /**
   * @description This method downloads a csv from the import wizard.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#downloadCsv()
   * @private
   * @returns {void}
   */
  private downloadCsv(fileKey: ('new' | 'previously-mapped' | 'split')): void {

    // Define our data container with its header row.
    const csvData: string[][] = [
      ['PeoId', 'ClientId', 'CustomerId', 'CustomerName', 'CustomerBrand']
    ];

    // Check for the new file key then add the additional columns.
    if (fileKey === 'new') {

      // Add the excluded column.
      csvData[0].push('ExcludedFromImport');

      // Add the print settings reconfigured column.
      csvData[0].push('PrintSettingsReconfigured');
    }

    // Iterate over the PEO API Credentials.
    for (const c of ((fileKey === 'new') ? this.newClientCustomers.data.filter((i: PeoClientApiModel): boolean =>
        this.clientCustomerToggles[this.getClientCustomerToggleKey(i)].saved ||
        this.clientCustomerToggles[this.getClientCustomerToggleKey(i)].exclude)
      : ((fileKey === 'previously-mapped')
        ? this.previouslyMappedClientCustomers.data : this.splitClientCustomers.data))) {

      // Define our row container.
      const row: string[] = [c.peoId, c.clientId, c.customerId.toString(), c.customerName, c.customerBrandName];

      // Check for the new file key then add the additional columns.
      if (fileKey === 'new') {

        // Add the excluded flag.
        row.push(this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].exclude ? 'Yes' : 'No');

        // Add the print settings reconfigured flag.
        row.push(this.savingNewClientCustomersIncludePrintSettings ? 'Yes' : 'No');
      }

      // Quote any values that contain commas.
      csvData.push(row.map((v: string): string => v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v));
    }

    // Define our blob.
    const blob: Blob = new Blob([csvData.map((r: string[]): string => r.join(',')).join('\n')],
      {type: 'text/csv;charset=utf-8;'});

    // Define our URL.
    const url: string = window.URL.createObjectURL(blob);

    // Define our anchor.
    const anchor: HTMLAnchorElement = document.createElement('a');

    // Set the anchor's attributes.
    anchor.href = url;

    // Set the anchor's download attribute.
    anchor.download = `prismhr-import-wizard-${fileKey}-client-customers-${Date.now()}.csv`;

    // Append the anchor to the document body.
    document.body.appendChild(anchor);

    // Click the anchor.
    anchor.click();

    // Remove the anchor from the document body.
    document.body.removeChild(anchor);

    // Revoke the URL.
    window.URL.revokeObjectURL(url);
  }

  /**
   * @description This method executes the import of the new client-customer mappings from the import wizard.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#importNewClientCustomers()
   * @private
   * @returns {void}
   */
  private runSaveNewCustomerClientMappings(): void {

    // Reset the toggles for all new client customers.
    this.newClientCustomers.data.filter((c: PeoClientApiModel): boolean =>
      !this.clientCustomerToggles[this.getClientCustomerToggleKey(c)].exclude)
      .forEach((c: PeoClientApiModel): void => {

        // Reset the saving flag in the toggles for the new client customer.
        this.updateSavingNewClientCustomerToggles(c, undefined, false, true);

        // Save the customer.
        this.peoClientService.save(c, this.savingNewClientCustomersIncludePrintSettings).subscribe({

          // Handle any errors that occur.
          error: (e: Error): void => {

            // Reset the flag in the toggles for the new client customer.
            this.updateSavingNewClientCustomerToggles(c, e, false, false);

            // Display a snack bar message to the user.
            this.snackbar.open(`Saving PEO Client-Customer Mapping with PEO ID ${c.peoId} and Client ID ${c.clientId} failed with:  ${e.message}`,
              'Dismiss', {
                duration: 5000,
                horizontalPosition: 'end',
                panelClass: ['snackbar-error'],
                verticalPosition: 'bottom'
              });
          },

          // Handle the next value of the observable.
          next: (client: PeoClientApiModel): void =>
            this.updateSavingNewClientCustomerToggles(client, undefined, true, false)
        });
      });
  }

  /**
   * @description This method is called to update the missing PEO toggles.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#updateMissingPeoToggles()
   * @param {string} peoId The PEO id to update the toggles for.
   * @param {Error} [error] The error to update the toggles with.
   * @param {boolean} [saved] The saved status to update the toggles with.
   * @param {boolean} [saving] The saving status to update the toggles with.
   * @private
   * @returns {void}
   */
  private updateMissingPeoToggles(peoId: string, error?: Error, saved?: boolean, saving?: boolean): void {

    // Update the missing PEO toggles.
    this.missingPeoToggles[peoId] = {

      // Reset the error if provided.
      error: error ?? this.missingPeoToggles[peoId].error,

      // Reset the saved flag if provided.
      saved: saved ?? this.missingPeoToggles[peoId].saved,

      // Reset the saving flag if provided.
      saving: saving ?? this.missingPeoToggles[peoId].saving
    };

    // Run the check missing PEO toggles check.
    this.checkMissingPeoToggles();
  }

  /**
   * @description This method is called to update the new client customer toggles.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#updateSavingNewClientCustomerToggles()
   * @param {PeoClientApiModel} client The client to update the toggles for.
   * @param {Error} [error] The error to update the toggles with.
   * @param {boolean} [saved] The saved status to update the toggles with.
   * @param {boolean} [saving] The saving status to update the toggles with.
   * @private
   * @returns {void}
   */
  private updateSavingNewClientCustomerToggles(client: PeoClientApiModel, error?: Error, saved?: boolean, saving?: boolean): void {

    // Localize the key for the client customer toggles.
    const key: string = this.getClientCustomerToggleKey(client);

    // Update the new client customer toggles.
    this.clientCustomerToggles[key] = {

      // Pass the checked flag through/
      checked: this.clientCustomerToggles[key].checked,

      // Pass the checking flag through.
      checking: this.clientCustomerToggles[key].checking,

      // Reset the error if provided.
      error: error ?? this.clientCustomerToggles[key].error,

      // Pass the exclude flag through.
      exclude: this.clientCustomerToggles[key].exclude,

      // Reset the saved flag if provided.
      saved: saved ?? this.clientCustomerToggles[key].saved,

      // Reset the saving flag if provided.
      saving: saving ?? this.clientCustomerToggles[key].saving,

      // Pass the unique flag through.
      unique: this.clientCustomerToggles[key].unique
    };

    // Run the check new client customer toggles check.
    this.checkNewClientCustomerToggles();
  }

  /**
   * @description This method is called to update the unique client customer toggles.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#updateUniqueClientCustomerToggles()
   * @param {PeoClientApiModel} client The client to update the toggles for.
   * @param {boolean} [checked] The checked status to update the toggles with.
   * @param {boolean} [checking] The checking status to update the toggles with.
   * @param {boolean} [unique] The unique status to update the toggles with.
   * @private
   * @returns {void}
   */
  private updateUniqueClientCustomerToggles(client: PeoClientApiModel, checked?: boolean, checking?: boolean, unique?: boolean): void {

    // Localize the key for the client customer toggles.
    const key: string = this.getClientCustomerToggleKey(client);

    // Update the unique client customer toggles.
    this.clientCustomerToggles[key] = {

      // Reset the checked flag if provided.
      checked: checked ?? this.clientCustomerToggles[key].checked,

      // Reset the checking flag if provided.
      checking: checking ?? this.clientCustomerToggles[key].checking,

      // Pass the error through.
      error: this.clientCustomerToggles[key].error,

      // Pass the exclude flag through.
      exclude: this.clientCustomerToggles[key].exclude,

      // Pass through the saved flag.
      saved: this.clientCustomerToggles[key].saved,

      // Pass through the saving flag.
      saving: this.clientCustomerToggles[key].saving,

      // Reset the unique flag if provided.
      unique: unique ?? this.clientCustomerToggles[key].unique
    };

    // Run the check unique client customer toggles check.
    this.checkUniqueClientCustomerToggles();
  }

  /**
   * @description This method is called to acknowledge the split client customers.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#acknowledgeSplitClientCustomers()
   * @param {MatStepper} stepper The stepper to navigate to the next step.
   * @protected
   * @returns {void}
   */
  protected acknowledgeSplitClientCustomers(stepper: MatStepper): void {

    // Open the confirmation dialog to gain the user's acknowledgment if there are split client-customers.
    if (this.totalSplitClientCustomers) this.dialog.open(ConfirmationDialogComponent, {

      // Set the dialog data to the acknowledgment message.
      data: {
        cancelText: 'Back to Review',
        confirmText: 'I Understand',
        message: `These client-customers will <strong>not</strong> be mapped in the database
          as we do not have a third point of identity &lbrack;uniqueness&rbrack; to match up with
          PrismHR.`,
        title: 'Split Client-Customer Acknowledgement'
      },
      disableClose: true,
      width: '800px'
    }).afterClosed().subscribe((acknowledged: boolean) => {

      // Check for the user's acknowledgment.
      if (acknowledged) {

        // Determine the previously mapped and new client-customers.
        this.determineNewAndPreviouslyMappedClientCustomers();

        // Navigate to the next step in the stepper.
        stepper.next();
      }
    });

    // Otherwise, continue to the next step in the stepper.
    else {

      // Determine the previously mapped and new client-customers.
      this.determineNewAndPreviouslyMappedClientCustomers();

      // Navigate to the next step in the stepper.
      stepper.next();
    }
  }

  /**
   * @description This method is called to check for missing PEOs on the PEO validation step.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#checkMissingPeo()
   * @protected
   * @returns {void}
   */
  protected checkMissingPeo(): void {

    // Set the loading status for the peo map to true.
    this.loadingPeoMap = true;

    // Reset the missing peo status.
    this.missingPeo = false;

    // Reset the missing peo saved status.
    this.missingPeoSaved = false;

    // Reset the missing peo saving status.
    this.missingPeoSaving = false;

    // Reset the missing peo toggles.
    this.missingPeoToggles = {};

    // Reset the missing peo map.
    this.peoMap = {};

    // Reset the missing peo form array.
    this.peoValidationFormGroup.controls.items = new FormArray<FormGroup<{
      id: FormControl<string>,
      name: FormControl<string>
    }>>([]);

    // Load the peo map.
    this.peoService.map().subscribe({

      // On completion, set the loading status for the peo map to false.
      complete: (): boolean => this.loadingPeoMap = false,

      // On success, set the peo map into the instance.
      next: (m: { [key: string]: PeoApiModel }) => {

        // Set the peo map into the instance.
        this.peoMap = m;

        // Map the PEOs to a list of strings representing their unique ids from the CSV data.
        const peoIds: string[] = [];

        // Iterate over the PEOS in the CSV data.
        for (let i = 0; i < this.csv.length; ++i) {

          // Localize the id from the row.
          const item: { [key: string]: any } = this.csv[i];

          // Localize the PEO id from the row.
          const peoId: string = item[this.csvHeaders[this.columnSelectionFormGroup.controls.peoIdColumn.value]] as string;

          // If the PEOS id is not in the list already, add it.
          if (!peoIds.includes(peoId)) peoIds.push(peoId);
        }

        // Localize the missing PEOs.
        const missingPeos: string[] = peoIds.filter((id: string): boolean => !this.peoMap[id]) ?? [];

        // Check for missing peos in the peo map.
        this.missingPeo = !!missingPeos.length;

        // Check for missing PEOs.
        if (this.missingPeo) {

          // Define our missing PEO toggles.
          const missingPeoToggles: { [key: string]: { error?: Error, saved: boolean, saving: boolean } } = {};

          // Iterate over the missing peos.
          for (let i = 0; i < missingPeos.length; ++i) {

            // Localize our form group for the form array.
            const missingFormGroup: FormGroup = new FormGroup<{
              id: FormControl<string>,
              name: FormControl<string>
            }>({

              // Add the peo id form control to the form group.
              id: new FormControl<string>(UtilityService.EmptyString, {

                // This form control is not nullable.
                nonNullable: true,

                // This form control is required to have a minimum length of 1.
                validators: [Validators.required]
              }),

              // Add the peo name form control to the form group.
              name: new FormControl<string>(UtilityService.EmptyString, {

                // This form control is not nullable.
                nonNullable: true,

                // This form control is required to have a minimum length of 1.
                validators: [Validators.required]
              })
            });

            // Set the peo id into the new form group id field.
            missingFormGroup.controls['id'].setValue(missingPeos[i]);

            // Add the missing form group to the form array.
            this.peoValidationFormGroup.controls.items.push(missingFormGroup);

            // Add the missing peo to the missing peo toggles.
            missingPeoToggles[missingPeos[i]] = {error: undefined, saved: false, saving: false};
          }

          // Reset the missing peo toggles into the instance.
          this.missingPeoToggles = missingPeoToggles;
        }

        // Otherwise, reset the saved status of the missing peos.
        else this.missingPeoSaved = true;
      },

      // On error, set the loading status for the peo map to false and show an error snackbar.
      error: (e: Error) => this.snackbar.open(`Failed to load the PEO Map with:  ${e.message}`,
        'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        })
    });
  }

  /**
   * @description This method clears the filter on the new client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#clearFilterOnNewClientCustomersTable()
   * @protected
   * @returns {void}
   */
  protected clearFilterOnNewClientCustomersTable(): void {

    // Clear the filter input.
    this.newClientCustomersFilterInput = UtilityService.EmptyString;

    // Clear the filter on the table.
    this.newClientCustomers.filter = UtilityService.EmptyString;

    // Check for a paginator, then navigate to the first page.
    this.newClientCustomers.paginator?.firstPage();
  }

  /**
   * @description This method clears the filter on the previously mapped client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#clearFilterOnPreviouslyMappedClientCustomersTable()
   * @protected
   * @returns {void}
   */
  protected clearFilterOnPreviouslyMappedClientCustomersTable(): void {

    // Clear the filter input.
    this.previouslyMappedClientCustomersFilterInput = UtilityService.EmptyString;

    // Clear the filter on the table.
    this.previouslyMappedClientCustomers.filter = UtilityService.EmptyString;

    // Check for a paginator, then navigate to the first page.
    this.previouslyMappedClientCustomers.paginator?.firstPage();
  }

  /**
   * @description This method clears the filter on the split client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#clearFilterOnSplitClientCustomersTable()
   * @protected
   * @returns {void}
   */
  protected clearFilterOnSplitClientCustomersTable(): void {

    // Clear the filter input.
    this.splitClientCustomersFilterInput = UtilityService.EmptyString;

    // Clear the filter on the table.
    this.splitClientCustomers.filter = UtilityService.EmptyString;

    // Check for a paginator, then navigate to the first page.
    this.splitClientCustomers.paginator?.firstPage();
  }

  /**
   * @description This method circulates the CSV header columns so no two column selections can be the same.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#circulateSelectedColumns()
   * @protected
   * @returns {void}
   */
  protected circulateSelectedColumns(): void {

    // Localize the selected index for the client id column.
    const clientIdIndex: number = this.columnSelectionFormGroup.controls.clientIdColumn.value;

    // Localize the selected index for the customer id column.
    const customerIdIndex: number = this.columnSelectionFormGroup.controls.customerIdColumn.value;

    // Localize the selected index for the peo id column.
    const peoIdIndex: number = this.columnSelectionFormGroup.controls.peoIdColumn.value;

    // Reset the filtered csv headers for the client id column.
    this.csvHeadersFilteredClientId = Object.fromEntries(this.csvHeaders.filter((h: string): boolean =>
      h === this.csvHeaders[clientIdIndex] || (h !== this.csvHeaders[customerIdIndex] && h !== this.csvHeaders[peoIdIndex]))
      .map((h: string): { i: number, h: string } => ({i: this.csvHeaders.indexOf(h), h}))
      .map((h: { i: number, h: string }): (number | string)[] => [h.i, h.h]));

    // Reset the filtered csv headers for the customer id column.
    this.csvHeadersFilteredCustomerId = Object.fromEntries(this.csvHeaders.filter((h: string): boolean =>
      h === this.csvHeaders[customerIdIndex] || (h !== this.csvHeaders[clientIdIndex] && h !== this.csvHeaders[peoIdIndex]))
      .map((h: string): { i: number, h: string } => ({i: this.csvHeaders.indexOf(h), h}))
      .map((h: { i: number, h: string }): (number | string)[] => [h.i, h.h]));

    // Reset the filtered csv headers for the peo id column.
    this.csvHeadersFilteredPeoId = Object.fromEntries(this.csvHeaders.filter((h: string): boolean =>
      h === this.csvHeaders[peoIdIndex] || (h !== this.csvHeaders[clientIdIndex] && h !== this.csvHeaders[customerIdIndex]))
      .map((h: string): { i: number, h: string } => ({i: this.csvHeaders.indexOf(h), h}))
      .map((h: { i: number, h: string }): (number | string)[] => [h.i, h.h]));
  }

  /**
   * @description This method is called when the user requests to start over on the import wizard.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#confirmStartOver()
   * @param {boolean} [skipConfirm] The flag to skip the confirmation dialog.
   * @protected
   * @returns {void}
   */
  protected confirmStartOver(skipConfirm: boolean = false): void {

    // Check for a confirmation skip then reload the window.
    if (skipConfirm) {

      // Reload the window.
      window.location.reload();

      // We're done, return.
      return;
    }

    // Open a confirmation dialog.
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Confirm Import Wizard Restart',
        message: 'Are you sure you want to start over? This will reset all progress and data.',
        confirmText: 'Start Over',
        cancelText: 'Cancel'
      },
      disableClose: true,
      width: '800px'
    }).afterClosed().subscribe((confirmed: boolean): void => {

      // Check for confirmation, then reload the window,
      if (confirmed) window.location.reload();
    });
  }

  /**
   * @description This method determines the new and previously mapped client-customers for the review step.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#determineNewAndPreviouslyMappedClientCustomers()
   * @protected
   * @returns {void}
   */
  protected determineNewAndPreviouslyMappedClientCustomers(): void {

    // Reset the loading status for the new client customers into the instance.
    this.loadingNewClientCustomers = true;

    // Reset the loading status for the previously mapped client customers into the instance.
    this.loadingPreviouslyMappedClientCustomers = true;

    // Reset the total number of new client customers into the instance.
    this.totalNewClientCustomers = 0;

    // Reset the total number of previously mapped client customers into the instance.
    this.totalPreviouslyMappedClientCustomers = 0;

    // Reset the new client customers into the instance.
    this.newClientCustomers = new MatTableDataSource<PeoClientApiModel>();

    // Reset the previously mapped client customers into the instance.
    this.previouslyMappedClientCustomers = new MatTableDataSource<PeoClientApiModel>();

    // Iterate over the new client customers to check for previously mapped client customers.
    this.clientCustomers.forEach((c: PeoClientApiModel): any =>
      this.clientCustomerToggles[this.getClientCustomerToggleKey(c)] =
        {checked: false, checking: true, error: undefined, exclude: false, saved: false, saving: false, unique: false});

    // Run the check for previously mapped client customers.
    this.peoClientService.loadMany(this.clientCustomers.map((c: PeoClientApiModel): PeoClientApiManyRequestModel =>
      ({peoId: c.peoId, clientId: c.clientId} as PeoClientApiManyRequestModel))).subscribe({

      // Handle any errors that occur.
      error: (e: Error): void => {

        // Reset the loading status for the new client customers into the instance.
        this.clientCustomers.forEach((c: PeoClientApiModel): void =>
          this.updateUniqueClientCustomerToggles(c, false, false));

        // Notify the user of the error.
        this.snackbar.open(`Loading Previously Mapped PEO Clients failed with:  ${e.message}`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          })
      },

      // Handle the next value of the observable.
      next: (l: PeoClientApiModel[]): void => this.clientCustomers.forEach((c: PeoClientApiModel): void =>
        this.updateUniqueClientCustomerToggles(c, true, false, !l.find((v: PeoClientApiModel): boolean =>
          this.getClientCustomerToggleKey(v) === this.getClientCustomerToggleKey(c))))
    });
  }

  /**
   * @description This method is called when the user requests to start the review step by determining the split customers.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#determineSplitClientCustomers()
   * @protected
   * @returns {void}
   */
  protected determineSplitClientCustomers(): void {

    // Reset the total number of split client customers.
    this.totalSplitClientCustomers = 0;

    // Reset the loading status for the split customers into the instance.
    this.loadingSplitClientCustomers = true;

    // Reset the split customers into the instance.
    this.splitClientCustomers.data = [];

    // Reset the new client customers into the instance.
    this.newClientCustomers.data = [];

    // Reset the client-customer toggles into the instance.
    this.clientCustomerToggles = {};

    // Localize the client id column from the column selection form group.
    const clientIdColumn: number = this.columnSelectionFormGroup.controls.clientIdColumn.value;

    // Localize the customer id column from the column selection form group.
    const customerIdColumn: number = this.columnSelectionFormGroup.controls.customerIdColumn.value;

    // Localize the peo id column from the column selection form group.
    const peoIdColumn: number = this.columnSelectionFormGroup.controls.peoIdColumn.value;

    // Define our client-customer container.
    let clientCustomers: PeoClientApiModel[] = [];

    // Iterate over the csv data.
    for (let i = 0; i < this.csv.length; ++i) {

      // Localize the row from the csv data.
      const row: { [key: string]: any } = this.csv[i];

      // Instantiate our peo client api model.
      const client: PeoClientApiModel = new PeoClientApiModel();

      // Set the peo id into the peo client api model.
      client.peoId = row[this.csvHeaders[peoIdColumn]];

      // Set the client id into the peo client api model.
      client.clientId = row[this.csvHeaders[clientIdColumn]];

      // Set the customer brand into the peo client api model.
      client.customerBrandName = this.customerMap?.[row[this.csvHeaders[customerIdColumn]]]?.brandName ?? 'Unmapped';

      // Set the customer id into the peo client api model.
      client.customerId = row[this.csvHeaders[customerIdColumn]] as number;

      // Set the customer name into the peo client api model.
      client.customerName = this.customerMap?.[row[this.csvHeaders[customerIdColumn]]]?.name;

      // Add the client-customer to the container.
      clientCustomers.push(client);
    }

    // Set the split client-customers into the instance.
    this.splitClientCustomers.data = UtilityService.GetDuplicates<PeoClientApiModel>(clientCustomers, 'peoId', 'clientId');

    // Set the total number of split client customers into the instance.
    this.totalSplitClientCustomers = this.splitClientCustomers.data.length;

    // Reset the split client-customer loading status into the instance.
    this.loadingSplitClientCustomers = false;

    // Reset the client-customers to the filtered out non split client-customers.
    this.clientCustomers = clientCustomers.filter((c: PeoClientApiModel): boolean =>
      !this.splitClientCustomers.data.includes(c));
  }

  /**
   * @description This method is called to download the new client-customers CSV.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#downloadNewClientCustomersCsv()
   * @protected
   * @returns {void}
   */
  protected downloadNewClientCustomersCsv(): void {

    // Reset the downloading flag in the instance.
    this.downloadingCsvNewClientCustomers = true;

    // Download the new client customers CSV.
    this.downloadCsv('new');

    // Reset the downloading flag in the instance.
    this.downloadingCsvNewClientCustomers = false;
  }

  /**
   * @description This method is called to download the previously mapped client-customers CSV.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#downloadPreviouslyMappedClientCustomersCsv()
   * @protected
   * @returns {void}
   */
  protected downloadPreviouslyMappedClientCustomersCsv(): void {

    // Reset the downloading flag in the instance.
    this.downloadingCsvPreviouslyMappedClientCustomers = true;

    // Download the previously mapped client customers CSV.
    this.downloadCsv('previously-mapped');

    // Reset the downloading flag in the instance.
    this.downloadingCsvPreviouslyMappedClientCustomers = false;
  }

  /**
   * @description This method is called to download the split client-customers CSV.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#downloadSplitClientCustomersCsv()
   * @protected
   * @returns {void}
   */
  protected downloadSplitClientCustomersCsv(): void {

    // Reset the downloading flag in the instance.
    this.downloadingCsvSplitClientCustomers = true;

    // Download the split client customers CSV.
    this.downloadCsv('split');

    // Reset the downloading flag in the instance.
    this.downloadingCsvSplitClientCustomers = false;
  }

  /**
   * @description This method filters the new client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#filterNewClientCustomersTable()
   * @param {Event} event The event that triggered the filter.
   * @protected
   * @returns {void}
   */
  protected filterNewClientCustomersTable(event: Event): void {

    // Reset the filter input.
    this.newClientCustomersFilterInput = (event.target as HTMLInputElement).value ?? UtilityService.EmptyString;

    // Ensure we have a filter input.
    if (!this.newClientCustomersFilterInput) return this.clearFilterOnNewClientCustomersTable();

    // Localize the columnar filter value.
    const filterValue: { [key: string]: string } = {};

    // Iterate over the filter columns and add the filter value to the filter input.
    this.filterColumns.forEach((c: string) => filterValue[c] = this.newClientCustomersFilterInput.trim().toLowerCase());

    // Apply the filter to the data table in the instance.
    this.newClientCustomers.filter = JSON.stringify(filterValue);

    // Check for a paginator, then navigate to the first page.
    this.newClientCustomers.paginator?.firstPage();
  }

  /**
   * @description This method filters the previously mapped client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#filterPreviouslyMappedClientCustomersTable()
   * @param {Event} event The event that triggered the filter.
   * @protected
   * @returns {void}
   */
  protected filterPreviouslyMappedClientCustomersTable(event: Event): void {

    // Reset the filter input.
    this.previouslyMappedClientCustomersFilterInput = (event.target as HTMLInputElement).value ?? UtilityService.EmptyString;

    // Ensure we have a filter input.
    if (!this.previouslyMappedClientCustomersFilterInput) return this.clearFilterOnPreviouslyMappedClientCustomersTable();

    // Localize the columnar filter value.
    const filterValue: { [key: string]: string } = {};

    // Iterate over the filter columns and add the filter value to the filter input.
    this.filterColumns.forEach((c: string) => filterValue[c] = this.previouslyMappedClientCustomersFilterInput.trim().toLowerCase());

    // Apply the filter to the data table in the instance.
    this.previouslyMappedClientCustomers.filter = JSON.stringify(filterValue);

    // Check for a paginator, then navigate to the first page.
    this.previouslyMappedClientCustomers.paginator?.firstPage();
  }

  /**
   * @description This method filters the split client customers table.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#filterSplitClientCustomersTable()
   * @param {Event} event The event that triggered the filter.
   * @protected
   * @returns {void}
   */
  protected filterSplitClientCustomersTable(event: Event): void {

    // Reset the filter input.
    this.splitClientCustomersFilterInput = (event.target as HTMLInputElement).value ?? UtilityService.EmptyString;

    // Ensure we have a filter input.
    if (!this.splitClientCustomersFilterInput) return this.clearFilterOnSplitClientCustomersTable();

    // Localize the columnar filter value.
    const filterValue: { [key: string]: string } = {};

    // Iterate over the filter columns and add the filter value to the filter input.
    this.filterColumns.forEach((c: string) => filterValue[c] = this.splitClientCustomersFilterInput.trim().toLowerCase());

    // Apply the filter to the data table in the instance.
    this.splitClientCustomers.filter = JSON.stringify(filterValue);

    // Check for a paginator, then navigate to the first page.
    this.splitClientCustomers.paginator?.firstPage();
  }

  /**
   * @description This method generates the client customer toggle key.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#getClientCustomerToggleKey()
   * @param {PeoClientApiModel} client The client to generate the key for.
   * @protected
   * @returns {string}
   */
  protected getClientCustomerToggleKey(client: PeoClientApiModel): string {

    // Return the key for the client customer toggles.
    return `${client.peoId}:${client.clientId}`;
  }

  /**
   * @description This method is called when the view has been initialized to load the customer map.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#loadCustomerMap()
   * @method
   * @name ImportWizardAdminPageComponent#loadCustomerMap()
   * @protected
   * @returns {void}
   */
  protected loadCustomerMap(): void {

    // Set the loading status for the customer map to true.
    this.loadingCustomerMap = true;

    // Localize the customer identifier column from the headers.
    const customerIdColumn: keyof {} =
      this.csvHeaders.at(this.columnSelectionFormGroup.controls.customerIdColumn.value) as keyof {};

    // Localize the unique customer identifiers.
    const identifiers: number[] = UtilityService.GetUnique(this.csv, customerIdColumn)
      .map((d: {}): number => d[customerIdColumn] as number);

    // Load the customer map.
    this.customerService.loadManyMap(identifiers).subscribe({

      // On completion, set the loading status for the customer map to false.
      complete: (): void => {

        // Determine the split client customers.
        this.determineSplitClientCustomers();

        // Reset the loading status for the customer map to false.
        this.loadingCustomerMap = false
      },

      // On success, set the customer map into the instance.
      next: (c: { [key: number]: CustomerApiModel }): { [key: number]: CustomerApiModel } => this.customerMap = c,

      // On error, set the loading status for the customer map to false and show an error snackbar.
      error: (e: Error) => this.snackbar.open(`Failed to load the Customer Map with:  ${e.message}`,
        'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        })
    });
  }

  /**
   * @description This method resets the column selection form group.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#resetColumnSelection()
   * @protected
   * @returns {void}
   */
  protected resetColumnSelection(): void {

    // Reset the selected value for the client id column.
    this.columnSelectionFormGroup.controls.clientIdColumn.reset();

    // Reset the selected value for the customer id column.
    this.columnSelectionFormGroup.controls.customerIdColumn.reset();

    // Reset the selected value for the peo id column.
    this.columnSelectionFormGroup.controls.peoIdColumn.reset();

    // Reset the filtered csv headers for the client id column.
    this.csvHeadersFilteredClientId = {};

    // Reset the filtered csv headers for the customer id column.
    this.csvHeadersFilteredCustomerId = {};

    // Reset the filtered csv headers for the peo id column.
    this.csvHeadersFilteredPeoId = {};

    // Iterate over the csv headers to reset the filtered csv headers.
    for (let i = 0; i < this.csvHeaders.length; ++i) {

      // Reset the filtered csv header for the client id column.
      this.csvHeadersFilteredClientId[i] = this.csvHeaders[i];

      // Reset the filtered csv header for the customer id column.
      this.csvHeadersFilteredCustomerId[i] = this.csvHeaders[i];

      // Reset the filtered csv header for the peo id column.
      this.csvHeadersFilteredPeoId[i] = this.csvHeaders[i];
    }
  }

  /**
   * @description This method is called when the user requests to save the missing PEOs on the PEO validation step.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#saveMissingPeo()
   * @protected
   * @returns {void}
   */
  protected saveMissingPeo(): void {

    // Reset the missing peo saving flag.
    this.missingPeoSaving = true;

    // Reset the missing peo saved flag.
    this.missingPeoSaved = false;

    // Iterate over the missing PEO items in the form array.
    for (let i = 0; i < this.peoValidationFormGroup.controls.items.length; ++i) {

      // Localize the form group for the missing PEO.
      const group: FormGroup = this.peoValidationFormGroup.controls.items.at(i) as FormGroup;

      // Localize the peo id from the form group.
      const peoId: string = group.controls['id'].value?.trim();

      // Check for an already saved PEO then skip the iteration.
      if (this.missingPeoToggles[peoId].saved) {

        // Reset the saving flag on the missing PEO's toggles.
        this.updateMissingPeoToggles(peoId, undefined, undefined, false);

        // Skip the iteration.
        continue;
      }

      // Localize the peo name from the form group.
      const name: string = group.controls['name'].value?.trim();

      // Set the saving flag on the missing PEO's toggles.
      this.updateMissingPeoToggles(peoId, undefined, undefined, true);

      // Save the missing PEO.
      this.peoService.save({name, peoId} as PeoApiModel).subscribe({

        // Handle any errors that occur.
        error: (e: Error): void => {

          // Reset the flag in the toggles for the missing PEO.
          this.updateMissingPeoToggles(peoId, e, false, false);

          // Display a snack bar message to the user.
          this.snackbar.open(`Saving PEO ${peoId} failed with:  ${e.message}`, 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          });
        },

        // Handle the next value of the observable.
        next: (p: PeoApiModel): void => this.updateMissingPeoToggles(p.peoId, undefined, true, false)
      });
    }
  }

  /**
   * @description This method is called when the user requests to save the new client-customer mappings.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#saveNewClientCustomers()
   * @protected
   * @returns {void}
   */
  protected saveNewClientCustomers(): void {

    // Reset the new client customer saving flag.
    this.savingNewClientCustomers = true;

    // Check for print setting inclusion and display a confirmation dialog.
    if (this.savingNewClientCustomersIncludePrintSettings) this.dialog.open(ConfirmationDialogComponent, {

      // Set the dialog data to the confirmation message.
      data: {
        title: 'Confirm Print Settings Reconfiguration',
        message: ('You\'ve chosen to include the 1095 Viewer Print Settings with the mapping import which means the print ' +
          'settings for the customers will be reset to the PrismHR values. Are you sure you want to proceed?'),
        confirmText: 'Yes, Import Client-Customers',
        cancelText: 'No'
      },
      disableClose: true,
      width: '800px'
    }).afterClosed().subscribe((confirmed: boolean): void => {

      // Check for confirmation, then save the new client-customers.
      if (confirmed) return this.runSaveNewCustomerClientMappings();

      // Reset the new client customer saving flag.
      this.savingNewClientCustomers = false;
    });

    // Otherwise, save the new client-customers.
    else this.runSaveNewCustomerClientMappings();
  }

  /**
   * @description This method is called when a csv file has been selected for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @name ImportWizardAdminPageComponent#selectedCsvFile()
   * @method
   * @param {HTMLInputElement} target The target input element that triggered the file selection.
   * @protected
   * @returns {void}
   */
  protected selectedCsvFile(target: HTMLInputElement): void {

    // Clear any csv data in the instance.
    this.csv = [];

    // Clear any existing csv file in the instance.
    this.csvFile = null;

    // Reset the csv file parsing status in the instance.
    this.csvFileParsing = false;

    // Reset the file upload progress into the instance.
    this.csvFileUploadProgress = 0;

    // Localize the files from the target
    const files: FileList | null = target.files;

    // Check for at least one selected file then reset the file into the instance.
    if (files && files.length > 0) this.csvFile = files[0];
  }

  /**
   * @description This method uploads and parses the csv file for the file upload step.
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#uploadCsvFile()
   * @param {HTMLInputElement} target The target input element that triggered the file selection.
   * @protected
   * @returns {void}
   */
  protected uploadCsvFile(target: HTMLInputElement): void {

    // Ensure we have a file to read.
    if (!this.csvFile || !target.files?.length) return;

    // Reset the csv headers into the instance.
    this.csvHeaders = [];

    // Reset the csv data into the instance.
    this.csv = [];

    // Reset the column selection form.
    this.resetColumnSelection();

    // Parse the csv file.
    this.parser.parse(target.files[0], {

      // We'll want the default csv delimiter of a comma.
      delimiter: ',',

      // The file will be read as text with utf-8 encoding.
      encoding: 'utf8',

      // We'll expect the first row to be the header so we'll set that in the configuration.
      header: true
    }).pipe().subscribe({

      // Handle the parsed results.
      next: (results: any[] | NgxCSVParserError): (void | MatSnackBarRef<TextOnlySnackBar>) => {

        // Check for an error in the results and throw it to be handled in the error callback.
        if (results instanceof NgxCSVParserError) return this.snackbar.open(`Failed to parse the CSV file with:  ${results.message}`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          });

        // Check for data rows and continue with the wizard.
        if (results.length) {

          // Set the CSV data into the instance.
          this.csv = results;

          // Reset the csv headers into the instance.
          this.csvHeaders = Object.keys(this.csv[0]);

          // Reset the column selection form.
          this.resetColumnSelection();

          // Reset the form field value.
          this.fileUploadFormGroup.controls.csv.setValue(true);

          // We're done, continue with the wizard.
          return;
        }

        // There are no data rows so display the confirmation dialog.
        this.dialog.open(ConfirmationDialogComponent, {

          // Set the dialog data to the no data message.
          data: {
            title: 'No Data Rows in CSV',
            message: 'The uploaded CSV file doesn\'t appear to have any data rows. Please check the file and try again.',
            confirmText: 'Go Back',
            displayCancelButton: false
          },
          disableClose: true,
          width: '800px'
        }).afterClosed().subscribe((): void => {

          // Reset the csv file in the instance.
          this.csvFile = null;

          // Reset the csv file parsing status in the instance.
          this.csvFileParsing = false;

          // Reset the file upload progress into the instance.
          this.csvFileUploadProgress = 0;

          // Reset the form field value.
          this.fileUploadFormGroup.controls.csv.setValue(false);

          // Reset the target value.
          target.value = '';
        });
      },

      // Bind to the error event.
      error: (e: NgxCSVParserError): MatSnackBarRef<TextOnlySnackBar> => this.snackbar.open(`Failed to parse the CSV file with:  ${e.message}`,
        'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        })
    });
  }

  /**
   * @description After View Init Lifecycle Hook
   * @memberof ImportWizardAdminPageComponent
   * @method
   * @name ImportWizardAdminPageComponent#ngAfterViewInit()
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Add our new client-customers to the table data source sorter.
    this.newClientCustomers.sort = this.newClientCustomersSort;

    // Add our previously mapped client-customers to the table data source sorter.
    this.previouslyMappedClientCustomers.sort = this.previouslyMappedClientCustomersSort;

    // Set our split client-customers to the table data source paginator.
    this.splitClientCustomers.paginator = this.paginators.toArray()[0];

    // Add our split client-customers to the table data source sorter.
    this.splitClientCustomers.sort = this.splitClientCustomersSort;
  }
}
